import { useState, useEffect, useRef } from 'react';
import axios from 'axios';
import './DataExtensions.css';
import Menu from '../Shared/Menu';
import usePageTitle from '../Shared/usePageTitle';

const API_URL    = '/rest/sfmc/data-extensions/v2';
const DETAIL_URL = '/rest/api/v1/data-extensions/detail';
const PAGE_SIZE  = 10;

// Chip label → backend `filter` value. Mirrors Automations FILTER_MAP.
const FILTER_MAP = {
    Sendable:       'Sendable',
    'Not Sendable': 'NotSendable',
};

/* ─── Static Fallback Data ─── */
const DATA_EXTENSIONS = [
    {
        id: 'de-1',
        name: 'WelcomeSeries_Contacts',
        sendable: true,
        category: 'Contact',
        journey: 'Welcome Series',
        folder: 'Marketing/Welcome',
        records: 142800,
        fields: 24,
        updated: 'Today',
        retention: '180 days',
        retentionLevel: 'medium',
        encrypted: false,
    },
    {
        id: 'de-2',
        name: 'WelcomeSeries_Exclusions',
        sendable: false,
        category: 'Reference',
        journey: 'Welcome Series',
        folder: 'Marketing/Suppression',
        records: 3200,
        fields: 8,
        updated: 'Yesterday',
        retention: '365 days',
        retentionLevel: 'low',
        encrypted: false,
    },
    {
        id: 'de-3',
        name: 'ReEngagement_Lapsed',
        sendable: true,
        category: 'Contact',
        journey: 'Re-engagement Campaign',
        folder: 'Marketing/ReEngagement',
        records: 58400,
        fields: 18,
        updated: 'Mar 1',
        retention: '90 days',
        retentionLevel: 'high',
        encrypted: false,
    },
    {
        id: 'de-4',
        name: 'PostPurchase_Buyers',
        sendable: true,
        category: 'Contact',
        journey: 'Post-Purchase Follow Up',
        folder: 'Marketing/PostPurchase',
        records: 203400,
        fields: 31,
        updated: 'Jan 14',
        retention: '365 days',
        retentionLevel: 'low',
        encrypted: true,
    },
    {
        id: 'de-5',
        name: 'PostPurchase_Products',
        sendable: false,
        category: 'Reference',
        journey: 'Post-Purchase Follow Up',
        folder: 'Marketing/PostPurchase',
        records: 48200,
        fields: 12,
        updated: 'Jan 14',
        retention: 'No expiry',
        retentionLevel: 'none',
        encrypted: false,
    },
    {
        id: 'de-6',
        name: 'Birthday_Eligible',
        sendable: true,
        category: 'Contact',
        journey: 'Birthday Journey',
        folder: 'Marketing/Birthday',
        records: 31200,
        fields: 14,
        updated: 'Today',
        retention: '365 days',
        retentionLevel: 'low',
        encrypted: false,
    },
];

/* ─── Helpers ─── */
function formatNumber(num) {
    if (num >= 1000000) return (num / 1000000).toFixed(1) + 'M';
    if (num >= 1000) return (num / 1000).toFixed(1) + 'K';
    return String(num);
}

function formatDateDE(dateStr) {
    if (!dateStr) return '';
    const d = new Date(dateStr);
    if (isNaN(d)) return dateStr;
    const now = new Date();
    if (d.toDateString() === now.toDateString()) return 'Today';
    const yesterday = new Date(now); yesterday.setDate(now.getDate() - 1);
    if (d.toDateString() === yesterday.toDateString()) return 'Yesterday';
    return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
}

function retentionLevel(days) {
    if (!days || days === 'No expiry' || days === 0) return 'none';
    const n = parseInt(days);
    if (isNaN(n)) return 'low';
    if (n <= 90) return 'high';
    if (n <= 180) return 'medium';
    return 'low';
}

function normalizeDE(item) {
    const isSendable = item.isSendable === 1 || item.isSendable === true || item.sendable === true;
    const retDays    = item.retentionPeriod || item.dataRetentionPeriod || item.retention || 'No expiry';
    const retLabel   = typeof retDays === 'number' ? (retDays > 0 ? `${retDays} days` : 'No expiry') : String(retDays);
    const rowCount   = item.rowCount || item.records || 0;

    return {
        id:              item.id || item.dataExtensionId || item.sfmcDeKey,
        dataExtensionId: item.dataExtensionId || item.id || '',
        sfmcDeKey:       item.sfmcDeKey || '',
        customerKey:     item.customerKey || '',
        name:            item.name || item.dataExtensionName || 'Unnamed DE',
        sendable:        isSendable,
        category:        isSendable ? 'Contact' : 'Reference',
        journey:         item.journey || item.folderName || '',
        folder:          item.folder || item.folderPath || '',
        records:         rowCount,
        fields:          item.fieldCount || item.fields || 0,
        updated:         formatDateDE(item.sfmcModifiedDate || item.modifiedDate || item.updatedAt),
        retention:       retLabel,
        retentionLevel:  retentionLevel(retDays),
        retentionAction: item.retentionAction || item.deleteAtEndOfRetentionPeriod ? 'Delete' : 'None',
        encrypted:       item.isEncrypted === 1 || item.encrypted === true,
        growth:          item.growth || '',
        growthNegative:  item.growthNegative || false,
        lastImport:      item.lastImport || formatDateDE(item.sfmcModifiedDate || item.modifiedDate) || '—',
        importMethod:    item.importMethod || '—',
        createdBy:       item.createdBy || item.owner || '—',
        created:         formatDateDE(item.sfmcCreatedDate || item.createdDate || item.createdAt) || '—',
        description:     item.description || '',
        fieldList:       (item.fields && Array.isArray(item.fields)) ? item.fields : [],
        _raw:            item,
    };
}

/* ─── DE Icon SVG ─── */
const DeIcon = ({ sendable }) => (
    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke={sendable ? '#3B82F6' : '#8B5CF6'} strokeWidth="1.8">
        <ellipse cx="12" cy="5" rx="9" ry="3" /><path d="M3 5v14a9 3 0 0 0 18 0V5" /><path d="M3 12a9 3 0 0 0 18 0" />
    </svg>
);

/* ─── DE Row Card ─── */
/* ─── DE Detail Panel (slideout) ─── */
function DEDetailPanel({ de, onClose }) {
    const [activeTab, setActiveTab] = useState('overview');
    const [showAllFields, setShowAllFields] = useState(false);
    const [detailFields, setDetailFields] = useState(null);
    const [loadingFields, setLoadingFields] = useState(false);

    useEffect(() => {
        if (!de || !de.id) return;
        setLoadingFields(true);
        // Postman expects dataExtensionId as a string, e.g. { "dataExtensionId": "1828488013" }
        const deId = de.dataExtensionId || de.id;
        const body = { dataExtensionId: String(deId) };
        axios.post(DETAIL_URL, body)
            .then(res => {
                const data = res.data;
                const fieldsArray = data?.fields || data?.fieldList || data?.dataExtensionFields || [];
                setDetailFields(Array.isArray(fieldsArray) ? fieldsArray : []);
            })
            .catch(() => setDetailFields([]))
            .finally(() => setLoadingFields(false));
    }, [de]);

    if (!de) return null;
    const rs = retentionStyleObj(de.retentionLevel);

    // If detail API returned fields, use them; otherwise fall back to static fieldList from list response
    const sourceFields = (detailFields && detailFields.length > 0) ? detailFields : (de.fieldList || []);

    const apiFields = sourceFields.map(f => {
        const baseType = f.fieldType || f.type || 'Text';
        const maxLen   = f.maxLength || f.length;
        const typeStr  = (baseType.toLowerCase() === 'text' || baseType.toLowerCase() === 'decimal') && maxLen
            ? `${baseType}(${maxLen}${f.scale ? `,${f.scale}` : ''})`
            : baseType;
        return {
            name:     f.name || f.fieldName || '—',
            type:     typeStr,
            pk:       f.isPrimaryKey === 1 || f.isPrimaryKey === true || f.pk === true,
            required: f.isRequired === 1 || f.isRequired === true || f.required === true || f.isNullable === 0,
            desc:     f.description || f.desc || f.defaultValue || '',
        };
    });
    const fields = apiFields;
    const shownFields = showAllFields ? fields : fields.slice(0, 10);

    // Show static count when loading or when API hasn't returned yet; show actual count after API returns
    let totalFieldCount;
    if (loadingFields || detailFields === null) {
        totalFieldCount = de.fields || 0;
    } else if (detailFields.length > 0) {
        totalFieldCount = detailFields.length;
    } else {
        totalFieldCount = (de.fieldList || []).length || de.fields || 0;
    }
    const remainingFields = totalFieldCount - shownFields.length;

    return (
        <>
            <div className="step-panel-backdrop show" onClick={onClose} />
            <div className="step-panel show">
                <div className="step-panel-header" style={{ padding: '22px 26px 18px', borderBottom: '1px solid #F1F5F9', background: '#FAFBFF' }}>
                    <div className="d-flex align-items-start justify-content-between w-100 mb-3">
                        <div className="d-flex align-items-center gap-3">
                            <div style={{ width: 44, height: 44, borderRadius: 12, display: 'flex', alignItems: 'center', justifyContent: 'center', background: de.sendable ? '#EFF6FF' : '#F8FAFC', border: `1px solid ${de.sendable ? '#BFDBFE' : '#E2E8F0'}` }}>
                                <DeIcon sendable={de.sendable} />
                            </div>
                            <div>
                                <div className="d-flex align-items-center gap-2 mb-2 flex-wrap">
                                    <span style={{ padding: '3px 10px', borderRadius: 20, fontSize: 11, fontWeight: 600, background: de.sendable ? '#EFF6FF' : '#F8FAFC', color: de.sendable ? '#2563EB' : '#64748B', border: `1px solid ${de.sendable ? '#BFDBFE' : '#E2E8F0'}` }}>{de.sendable ? 'Sendable' : 'Not Sendable'}</span>
                                    {de.encrypted && <span style={{ padding: '3px 10px', borderRadius: 20, fontSize: 11, fontWeight: 600, background: '#FFF7ED', color: '#C2410C', border: '1px solid #FED7AA' }}>🔒 Encrypted</span>}
                                </div>
                                <h2 style={{ fontWeight: 800, fontSize: 20, color: '#0F172A', margin: 0, wordBreak: 'break-word' }}>{de.name}</h2>
                            </div>
                        </div>
                        <button className="step-panel-close" onClick={onClose}>✕</button>
                    </div>
                    <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 10, width: '100%' }}>
                        <div style={{ background: '#F8FAFC', border: '1px solid #F1F5F9', borderRadius: 10, padding: '10px 12px' }}>
                            <p style={{ fontSize: 9, color: '#94A3B8', textTransform: 'uppercase', letterSpacing: '0.07em', marginBottom: 3 }}>Total Records</p>
                            <p style={{ fontWeight: 800, fontSize: 15, color: '#6366F1', margin: 0 }}>{formatNumber(de.records)}</p>
                        </div>
                        <div style={{ background: '#F8FAFC', border: '1px solid #F1F5F9', borderRadius: 10, padding: '10px 12px' }}>
                            <p style={{ fontSize: 9, color: '#94A3B8', textTransform: 'uppercase', letterSpacing: '0.07em', marginBottom: 3 }}>Fields</p>
                            <p style={{ fontWeight: 800, fontSize: 15, color: '#0EA5E9', margin: 0 }}>{de.fields}</p>
                        </div>
                        <div style={{ background: '#F8FAFC', border: '1px solid #F1F5F9', borderRadius: 10, padding: '10px 12px' }}>
                            <p style={{ fontSize: 9, color: '#94A3B8', textTransform: 'uppercase', letterSpacing: '0.07em', marginBottom: 3 }}>Last Updated</p>
                            <p style={{ fontWeight: 800, fontSize: 13, color: '#0F172A', margin: 0 }}>{de.updated}</p>
                        </div>
                    </div>
                </div>
                <div className="step-panel-tabs d-flex">
                    <button className={`modal-tab${activeTab === 'overview' ? ' active' : ''}`} onClick={() => setActiveTab('overview')}>Overview</button>
                    <button className={`modal-tab${activeTab === 'fields' ? ' active' : ''}`} onClick={() => setActiveTab('fields')}>Fields ({totalFieldCount})</button>
                </div>
                <div className="step-panel-body" style={{ padding: '20px 26px' }}>
                    {activeTab === 'overview' && (
                        <>
                            <div style={{ marginBottom: 24 }}>
                                <p style={{ fontSize: 11, fontWeight: 700, color: '#0F172A', textTransform: 'uppercase', letterSpacing: '0.07em', marginBottom: 8 }}>Record Growth</p>
                                <div style={{ background: '#F8FAFC', border: '1px solid #F1F5F9', borderRadius: 12, padding: '14px 16px', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                                    <div>
                                        <p style={{ fontSize: 10, color: '#94A3B8', textTransform: 'uppercase', letterSpacing: '0.07em', marginBottom: 4 }}>Total Records</p>
                                        <p style={{ fontWeight: 800, fontSize: 22, color: '#0F172A', margin: 0 }}>{formatNumber(de.records)}</p>
                                    </div>
                                    {de.growth && <span style={{ fontSize: 12, fontWeight: 700, padding: '4px 12px', borderRadius: 20, color: de.growthNegative ? '#EF4444' : '#10B981', background: de.growthNegative ? '#FEF2F2' : '#ECFDF5', border: `1px solid ${de.growthNegative ? '#FECACA' : '#A7F3D0'}` }}>{de.growth}</span>}
                                </div>
                            </div>
                            <div style={{ marginBottom: 24 }}>
                                <p style={{ fontSize: 11, fontWeight: 700, color: '#0F172A', textTransform: 'uppercase', letterSpacing: '0.07em', marginBottom: 8 }}>Data Retention Policy</p>
                                <div style={{ borderRadius: 12, padding: '16px 18px', display: 'flex', gap: 16, alignItems: 'center', background: rs.bg, border: `1px solid ${rs.border}` }}>
                                    <div style={{ width: 38, height: 38, borderRadius: 10, display: 'flex', alignItems: 'center', justifyContent: 'center', background: rs.iconBg, border: `1px solid ${rs.border}`, flexShrink: 0 }}>
                                        <svg width="17" height="17" viewBox="0 0 24 24" fill="none" stroke={rs.iconStroke} strokeWidth="2"><circle cx="12" cy="12" r="10" /><polyline points="12,6 12,12 16,14" /></svg>
                                    </div>
                                    <div>
                                        <p style={{ fontSize: 14, fontWeight: 700, color: rs.titleColor, margin: 0 }}>{de.retention}</p>
                                        <p style={{ fontSize: 12, color: rs.textColor, marginTop: 2 }}>On expiry: <strong>{de.retentionAction || 'None'}</strong></p>
                                    </div>
                                </div>
                            </div>
                            <div style={{ marginBottom: 24 }}>
                                <p style={{ fontSize: 11, fontWeight: 700, color: '#0F172A', textTransform: 'uppercase', letterSpacing: '0.07em', marginBottom: 8 }}>Details</p>
                                <div style={{ border: '1px solid #F1F5F9', borderRadius: 12, overflow: 'hidden' }}>
                                    {[
                                        { label: 'Journey', value: de.journey || '—' },
                                        { label: 'Folder', value: de.folder || '—' },
                                        { label: 'Created by', value: de.createdBy },
                                        { label: 'Created', value: de.created },
                                        { label: 'Encryption', value: de.encrypted ? 'Enabled' : 'Disabled' },
                                    ].map((r, i) => (
                                        <div key={r.label} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '11px 16px', borderBottom: '1px solid #F1F5F9', background: i % 2 === 1 ? '#FAFBFF' : '#fff' }}>
                                            <span style={{ fontSize: 12, color: '#94A3B8', fontWeight: 500 }}>{r.label}</span>
                                            <span style={{ fontSize: 13, color: '#0F172A', fontWeight: 600, textAlign: 'right' }}>{r.value}</span>
                                        </div>
                                    ))}
                                </div>
                            </div>
                        </>
                    )}
                    {activeTab === 'fields' && (
                        <>
                            <p style={{ fontSize: 12, color: '#94A3B8', marginBottom: 14 }}>
                                {loadingFields ? 'Loading fields…' : `Showing ${shownFields.length} of ${totalFieldCount} fields`}
                            </p>
                            <div className="d-flex flex-column gap-3">
                                {shownFields.map((f, i) => (
                                    <div key={i} style={{ background: '#fff', border: '1px solid #E2E8F0', borderRadius: 12, padding: '14px 16px' }}>
                                        <div className="d-flex justify-content-between align-items-start mb-2">
                                            <div className="d-flex align-items-center gap-2">
                                                <span style={{ fontWeight: 700, fontSize: 13, color: '#0F172A' }}>{f.name}</span>
                                                {f.pk && <span style={{ fontSize: 10, fontWeight: 700, color: '#6366F1', background: '#EEF2FF', border: '1px solid #C7D2FE', padding: '2px 7px', borderRadius: 8 }}>PK</span>}
                                                {f.required && <span style={{ fontSize: 10, fontWeight: 600, color: '#DC2626', background: '#FEF2F2', border: '1px solid #FECACA', padding: '2px 7px', borderRadius: 8 }}>Required</span>}
                                            </div>
                                            <span style={{ padding: '2px 8px', borderRadius: 6, fontSize: 11, fontWeight: 600, fontFamily: 'monospace', background: '#F0F9FF', color: '#0369A1', border: '1px solid #BAE6FD' }}>{f.type}</span>
                                        </div>
                                        {f.desc && <p style={{ fontSize: 12, color: '#64748B', margin: 0, lineHeight: 1.5, marginTop: 6 }}>{f.desc}</p>}
                                    </div>
                                ))}
                                {remainingFields > 0 && (
                                    <button
                                        onClick={() => setShowAllFields(true)}
                                        style={{
                                            textAlign: 'center', padding: 12, color: '#6366F1', fontSize: 12, fontWeight: 600,
                                            background: '#F8FAFC', border: '1px dashed #C7D2FE', borderRadius: 10,
                                            cursor: 'pointer', width: '100%',
                                        }}
                                    >
                                        + {remainingFields} more fields — click to show all
                                    </button>
                                )}
                                {showAllFields && fields.length > 10 && (
                                    <button
                                        onClick={() => setShowAllFields(false)}
                                        style={{
                                            textAlign: 'center', padding: 8, color: '#94A3B8', fontSize: 12, fontWeight: 600,
                                            background: 'transparent', border: 'none', cursor: 'pointer', width: '100%',
                                        }}
                                    >
                                        Show less
                                    </button>
                                )}
                            </div>
                        </>
                    )}
                </div>
            </div>
        </>
    );
}

function retentionStyleObj(level) {
    if (level === 'high') return { bg: '#FEF2F2', border: '#FECACA', iconBg: '#FEE2E2', iconStroke: '#DC2626', titleColor: '#991B1B', textColor: '#DC2626' };
    if (level === 'medium') return { bg: '#FFFBEB', border: '#FDE68A', iconBg: '#FEF3C7', iconStroke: '#D97706', titleColor: '#92400E', textColor: '#B45309' };
    return { bg: '#ECFDF5', border: '#A7F3D0', iconBg: '#D1FAE5', iconStroke: '#059669', titleColor: '#166534', textColor: '#059669' };
}

/* ─── DE Row Card ─── */
function DERowCard({ de, onClick }) {
    const accentClass = de.sendable ? 'de-accent-sendable' : 'de-accent-reference';
    const iconClass   = de.sendable ? 'de-icon-sendable' : 'de-icon-reference';
    const badgeClass  = de.sendable ? 'de-badge-sendable' : 'de-badge-reference';
    const badgeLabel  = de.sendable ? 'Sendable' : 'Not Sendable';

    return (
        <div className="de-row-card" onClick={onClick} style={{ cursor: 'pointer' }}>
            <div className={`de-accent-stripe ${accentClass}`} />
            <div className="de-card-content">
                <div className="d-flex align-items-center gap-3 flex-grow-1" style={{ minWidth: 0 }}>
                    <div className={`de-icon-box ${iconClass}`}>
                        <DeIcon sendable={de.sendable} />
                    </div>
                    <div style={{ minWidth: 0 }}>
                        <div className="d-flex align-items-center gap-2 mb-1 flex-wrap">
                            <h3 className="de-card-title">{de.name}</h3>
                            {de.encrypted && <span className="de-encrypted-icon">🔒</span>}
                        </div>
                        <div className="d-flex align-items-center gap-2 flex-wrap">
                            <span className={`de-badge ${badgeClass}`}>{badgeLabel}</span>
                            <span className="de-journey-text">{de.journey}</span>
                            <span className="de-separator">&middot;</span>
                            <span className="de-folder-text">{de.folder}</span>
                        </div>
                    </div>
                </div>
                <div className="de-stats-container">
                    <div className="de-stat-item">
                        <p className="de-stat-label">Records</p>
                        <p className="de-stat-value" style={{ color: '#6366F1' }}>{formatNumber(de.records)}</p>
                    </div>
                    <div className="de-stat-item">
                        <p className="de-stat-label">Fields</p>
                        <p className="de-stat-value" style={{ color: '#0EA5E9' }}>{de.fields}</p>
                    </div>
                    <div className="de-stat-item">
                        <p className="de-stat-label">Updated</p>
                        <p className="de-stat-value">{de.updated}</p>
                    </div>
                    <div className="de-stat-item">
                        <p className="de-stat-label">Retention</p>
                        <span className={`retention-badge retention-${de.retentionLevel}`}>{de.retention}</span>
                    </div>
                    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="#CBD5E1" strokeWidth="2.5" className="de-chevron">
                        <path d="M9 18l6-6-6-6" />
                    </svg>
                </div>
            </div>
        </div>
    );
}

/* ─── Main Page ─── */
function DataExtensions() {
    usePageTitle('Data Extensions');
    const [allDEs,         setAllDEs]         = useState([]);
    const [loading,        setLoading]        = useState(true);
    const [error,          setError]          = useState(null);
    const [searchQuery,    setSearchQuery]    = useState('');
    const [typeFilter,     setTypeFilter]     = useState('All');
    const [selectedDE,     setSelectedDE]     = useState(null);
    const [page,           setPage]           = useState(1);
    const [hasMore,        setHasMore]        = useState(true);
    const [loadingMore,    setLoadingMore]    = useState(false);
    const [totalCount,     setTotalCount]     = useState(0);
    const [kpis,           setKpis]           = useState({
        sendableCount: null, totalRecords: null, encryptedCount: null, highRiskCount: null,
    });
    const [refreshing,     setRefreshing]     = useState(false);
    const sentinelRef      = useRef(null);
    const loadingRef       = useRef(false);
    const firstLoadDoneRef = useRef(false);
    const pageRef          = useRef(1);
    const hasMoreRef       = useRef(true);

    const fetchPage = (p, opts = {}) => {
        if (loadingRef.current) return;
        loadingRef.current = true;
        if (p === 1) {
            if (firstLoadDoneRef.current) setRefreshing(true);
            else setLoading(true);
        } else {
            setLoadingMore(true);
        }
        const searchArg = opts.search ?? searchQuery;
        const typeArg   = opts.type   ?? typeFilter;
        const params = new URLSearchParams({ page: String(p), pageSize: String(PAGE_SIZE) });
        params.set('search', searchArg || '');
        params.set('filter', typeArg && typeArg !== 'All' ? (FILTER_MAP[typeArg] || typeArg) : '');

        axios.get(`${API_URL}?${params.toString()}`)
            .then(res => {
                const data = res.data;
                console.log('[DataExtensions] page', p, 'response:', data);
                const raw   = Array.isArray(data) ? data : (data?.items || data?.dataExtensions || data?.data || []);
                const list  = raw.map(normalizeDE);
                const total = typeof data?.total === 'number' ? data.total : null;
                const size  = typeof data?.pageSize === 'number' ? data.pageSize : PAGE_SIZE;

                const isUnfiltered = !searchArg && (!typeArg || typeArg === 'All');
                if (total != null && isUnfiltered) setTotalCount(total);
                setKpis({
                    sendableCount:  typeof data?.sendableCount  === 'number' ? data.sendableCount  : null,
                    totalRecords:   typeof data?.totalRecords   === 'number' ? data.totalRecords   : null,
                    encryptedCount: typeof data?.encryptedCount === 'number' ? data.encryptedCount : null,
                    highRiskCount:  typeof data?.highRiskCount  === 'number' ? data.highRiskCount  : null,
                });
                const more = total != null ? (p * size) < total : raw.length === size;
                hasMoreRef.current = more;
                pageRef.current = p;
                setHasMore(more);

                setAllDEs(prev => (p === 1 ? list : [...prev, ...list]));
            })
            .catch(err => setError(err.response?.data?.message || err.message))
            .finally(() => {
                loadingRef.current = false;
                if (p === 1) {
                    setLoading(false);
                    setRefreshing(false);
                    firstLoadDoneRef.current = true;
                } else {
                    setLoadingMore(false);
                }
            });
    };

    useEffect(() => {
        const t = setTimeout(() => {
            pageRef.current = 1;
            hasMoreRef.current = true;
            setPage(1);
            setHasMore(true);
            loadingRef.current = false;
            fetchPage(1, { search: searchQuery, type: typeFilter });
        }, searchQuery ? 300 : 0);
        return () => clearTimeout(t);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [searchQuery, typeFilter]);

    useEffect(() => {
        const node = sentinelRef.current;
        if (!node) return;
        if (!hasMore || loadingMore) return;
        const io = new IntersectionObserver(entries => {
            if (!entries[0]?.isIntersecting) return;
            if (loadingRef.current || !hasMoreRef.current) return;
            const next = pageRef.current + 1;
            setPage(next);
            fetchPage(next, { search: searchQuery, type: typeFilter });
        }, { rootMargin: '300px' });
        io.observe(node);
        return () => io.disconnect();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [hasMore, loadingMore, allDEs.length, searchQuery, typeFilter]);

    // Server-side filtering — `allDEs` is already the filtered/paginated page from the API.
    const DES = allDEs;
    const filtered = DES;

    // KPI calculations — prefer server-provided values; fall back to what we have loaded.
    const totalDEs       = totalCount || DES.length;
    const sendableCount  = kpis.sendableCount  ?? DES.filter(d => d.sendable).length;
    const totalRecords   = kpis.totalRecords   ?? DES.reduce((s, d) => s + (d.records || 0), 0);
    const encryptedCount = kpis.encryptedCount ?? DES.filter(d => d.encrypted).length;
    const highRiskCount  = kpis.highRiskCount  ?? DES.filter(d => d.retentionLevel === 'high').length;

    return (
        <>
            <Menu />

            <div className="container fade-up container-main">
                <div className="page-header mb-4">
                    <h1>Data Extensions</h1>
                    <p>All data extensions, field schemas, retention policies and import history</p>
                </div>

                {loading && <div className="text-center py-5"><p>Loading data extensions...</p></div>}
                {error && <div className="text-center py-5"><p className="text-danger">Failed to load: {error}</p></div>}

                {!loading && !error && <>
                {/* KPI Tiles */}
                <div className="row g-3 mb-4">
                    <div className="col-6 col-md-3">
                        <div className="kpi-card d-flex align-items-start gap-3">
                            <div className="kpi-icon" style={{ background: '#6366F114' }}>&#x2B21;</div>
                            <div>
                                <p className="kpi-label">Total DEs</p>
                                <p className="kpi-value">{totalDEs}</p>
                                <p className="kpi-subtext">{sendableCount} sendable</p>
                            </div>
                        </div>
                    </div>
                    <div className="col-6 col-md-3">
                        <div className="kpi-card d-flex align-items-start gap-3">
                            <div className="kpi-icon" style={{ background: '#10B98114' }}>&#x25C8;</div>
                            <div>
                                <p className="kpi-label">Total Records</p>
                                <p className="kpi-value">{formatNumber(totalRecords)}</p>
                                <p className="kpi-subtext">across all extensions</p>
                            </div>
                        </div>
                    </div>
                    <div className="col-6 col-md-3">
                        <div className="kpi-card d-flex align-items-start gap-3">
                            <div className="kpi-icon" style={{ background: '#8B5CF614' }}>&#x1F512;</div>
                            <div>
                                <p className="kpi-label">Encrypted DEs</p>
                                <p className="kpi-value">{encryptedCount}</p>
                                <p className="kpi-subtext">data at rest encrypted</p>
                            </div>
                        </div>
                    </div>
                    <div className="col-6 col-md-3">
                        <div className="kpi-card d-flex align-items-start gap-3">
                            <div className="kpi-icon" style={{ background: '#EF444414' }}>&#x26A0;</div>
                            <div>
                                <p className="kpi-label">High Risk Retention</p>
                                <p className="kpi-value">{highRiskCount}</p>
                                <p className="kpi-subtext">expiry &le; 90 days</p>
                            </div>
                        </div>
                    </div>
                </div>

                {/* Filters */}
                <div className="d-flex gap-3 mb-3 flex-wrap align-items-center">
                    <div className="search-wrapper flex-grow-1">
                        <svg className="search-icon" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#94A3B8" strokeWidth="2">
                            <circle cx="11" cy="11" r="8" /><path d="m21 21-4.35-4.35" />
                        </svg>
                        <input
                            type="text"
                            className="search-input"
                            placeholder="Search by name, journey or folder..."
                            value={searchQuery}
                            onChange={e => setSearchQuery(e.target.value)}
                        />
                    </div>
                    <div className="filter-buttons">
                        {['All', 'Sendable', 'Not Sendable'].map(f => (
                            <button
                                key={f}
                                className={`filter-btn${typeFilter === f ? ' active' : ''}`}
                                onClick={() => setTypeFilter(f)}
                            >
                                {f}
                            </button>
                        ))}
                    </div>
                </div>

                {/* Results count */}
                <p className="de-results-count mb-3">
                    Showing <strong>{filtered.length}</strong> Records
                    {refreshing && ' · refreshing…'}
                </p>

                {/* DE Cards */}
                {filtered.length > 0 ? (
                    <div className="d-flex flex-column gap-3">
                        {filtered.map(de => (
                            <DERowCard key={de.id} de={de} onClick={() => setSelectedDE(de)} />
                        ))}
                    </div>
                ) : (
                    <div className="empty-state">
                        <div className="empty-state-icon">🔭</div>
                        <h3 className="empty-state-title">No data extensions found</h3>
                        <p className="empty-state-text">No data extensions match your filters.</p>
                    </div>
                )}

                {/* Infinite-scroll sentinel */}
                {hasMore && (
                    <div ref={sentinelRef} className="text-center py-3">
                        {loadingMore && <p style={{ fontSize: 12, color: '#94A3B8', margin: 0 }}>Loading more…</p>}
                    </div>
                )}
                </>}
            </div>

            {selectedDE && <DEDetailPanel de={selectedDE} onClose={() => setSelectedDE(null)} />}
        </>
    );
}

export default DataExtensions;
